import React, { useEffect } from "react";
import { Phone, MapPin, Clock, ArrowUpRight, FileText, ExternalLink } from "lucide-react";
import { PRACTICE } from "@/data/site";
import { useT } from "@/i18n/LanguageContext";
import SunDivider from "@/components/site/SunDivider";
import ContactForm from "@/components/site/ContactForm";
import Seo from "@/components/site/Seo";
import { breadcrumbSchema } from "@/lib/seo";

export default function ContactPage() {
  const { t } = useT();
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  return (
    <article data-testid="contact-page">
      <Seo
        title="Contact Us"
        description="Contact Smiles of Loudoun in Leesburg, VA. Call 571-510-3034 or send us a message — we respond within one business day."
        path="/contact"
        jsonLd={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Contact", path: "/contact" },
        ])}
      />
      <section className="px-6 md:px-10 lg:px-16 pt-14 md:pt-20 pb-10 md:pb-14 text-center">
        <div className="font-mono-eyebrow text-[var(--text-muted)]">
          {t("Contact · Leesburg, VA")}
        </div>
        <h1 className="font-serif-display text-[var(--primary)] mt-4 text-[48px] md:text-[80px] leading-[1] tracking-tight max-w-4xl mx-auto">
          {t("Let's start a")} <span className="italic text-[var(--accent)]">{t("conversation")}</span>.
        </h1>
        <SunDivider className="mt-6" />
        <p className="mt-5 max-w-xl mx-auto text-[var(--text-muted)] text-[17px]">
          {t("Send us a quick note, give us a call, or stop by — whichever feels most like you. We respond within one business day.")}
        </p>
      </section>

      <section className="px-6 md:px-10 lg:px-16 pb-20 md:pb-28">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-7">
            <ContactForm />
          </div>

          <aside className="lg:col-span-5 space-y-5">
            <div className="bg-[var(--primary)] text-[var(--bg)] rounded-3xl p-7 md:p-8">
              <div className="font-mono-eyebrow text-[var(--accent)]">
                {t("Reach us directly")}
              </div>
              <a
                href={PRACTICE.phoneHref}
                data-testid="contact-page-phone"
                className="mt-4 flex items-center gap-3 text-2xl md:text-3xl font-serif-display hover:text-[var(--accent)]"
              >
                <Phone className="w-6 h-6" /> {PRACTICE.phone}
              </a>
              <a
                href={PRACTICE.mapUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-page-address"
                className="mt-4 flex items-start gap-3 text-[var(--bg)]/85 hover:text-[var(--accent)]"
              >
                <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <span>{PRACTICE.address}</span>
              </a>
            </div>

            <div className="bg-[var(--surface)] border border-[var(--border)] rounded-3xl p-7 md:p-8">
              <div className="flex items-center gap-2 font-mono-eyebrow text-[var(--text-muted)]">
                <Clock className="w-4 h-4" /> {t("Office hours")}
              </div>
              <ul className="mt-4 space-y-2">
                {PRACTICE.hours.map((h) => (
                  <li
                    key={h.d}
                    className="flex justify-between text-[var(--text-main)]/85"
                  >
                    <span className="text-[var(--text-muted)]">{t(h.d)}</span>
                    <span>{t(h.t)}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-[var(--surface)] border border-[var(--border)] rounded-3xl p-7 md:p-8 space-y-3">
              <a
                href={PRACTICE.appointmentUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-page-book-online"
                className="flex items-center justify-between gap-3 text-[var(--primary)] hover:text-[var(--accent)]"
              >
                <span className="font-serif-display text-xl">
                  Request appointment online
                </span>
                <ArrowUpRight className="w-5 h-5" />
              </a>
              <div className="h-px bg-[var(--border)]" />
              <a
                href={PRACTICE.payOnlineUrl}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-between gap-3 text-[var(--primary)] hover:text-[var(--accent)]"
              >
                <span className="flex items-center gap-2">
                  <ExternalLink className="w-4 h-4" /> Pay online
                </span>
                <ArrowUpRight className="w-5 h-5" />
              </a>
              <div className="h-px bg-[var(--border)]" />
              <a
                href={PRACTICE.referralUrl}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-between gap-3 text-[var(--primary)] hover:text-[var(--accent)]"
              >
                <span className="flex items-center gap-2">
                  <FileText className="w-4 h-4" /> Refer a patient (PDF)
                </span>
                <ArrowUpRight className="w-5 h-5" />
              </a>
            </div>
          </aside>
        </div>
      </section>

      {/* Map */}
      <section className="px-6 md:px-10 lg:px-16 pb-20">
        <a
          href={PRACTICE.mapUrl}
          target="_blank"
          rel="noreferrer"
          data-testid="contact-page-map"
          className="block overflow-hidden rounded-3xl border border-[var(--border)]"
        >
          <iframe
            title="Smiles of Loudoun on Google Maps"
            src="https://www.google.com/maps?q=430+Harrison+Street+SE,+Leesburg,+VA+20175&output=embed"
            width="100%"
            height="420"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </a>
      </section>
    </article>
  );
}
